import {html, nothing, type TemplateResult} from 'lit';
import {live} from 'lit/directives/live.js';
import type {Character} from '../types';
import type {Label} from '../learner-model';
import {GOALS, type Goal} from '../progress-store';
import type {CategoryBreakdown} from '../quiz-controller';
import {checkboxOption} from './quiz-controls';

export interface ProgressPanelProps {
  breakdown: CategoryBreakdown[];
  weak: {c: Character; label: Label}[];
  totals: {answered: number; correct: number; days: number; mastered: number};
  /** 直近 7 日（古い順）に学習したか */
  recentDays: boolean[];
  goal: Goal;
  recordingAvailable: boolean;
  recordingEnabled: boolean;
  recordingReadOnly: boolean;
  confirmingClear: boolean;
  clearedCount: number | null;
  clearedPersisted: boolean;
  /** 読み方から字を選ぶ向きを混ぜるか */
  reverseEnabled: boolean;
  onGoal: (goal: Goal) => void;
  onReverse: (enabled: boolean) => void;
  onRecording: (enabled: boolean) => void;
  onRequestClear: () => void;
  onCancelClear: () => void;
  onConfirmClear: () => void;
  onUndoClear: () => void;
}

const HEADING_ID = 'dq-progress-heading';

/**
 * 学習の記録パネル。出題ボックスの「記録」で開き、開いたときだけ描画する
 * （既定で閉じているので初期描画の高さに影響しない）。
 *
 * 数えるのは学習の事実である量（問題数・定着字数・学習した日数）だけ。
 * 減点・途切れ・降格は書かない。弱点の一覧は赤で並べない。
 */
export function progressPanel(p: ProgressPanelProps): TemplateResult {
  const recorded = p.breakdown.reduce((s, r) => s + r.total - r.unseen, 0);
  // 正答率は 1 問も答えていないうちは出さない。初めて開いた人が最初に見る数字が「0%」になる
  const accuracyText =
    p.totals.answered > 0 ? ` ・ 正答率 ${Math.round((100 * p.totals.correct) / p.totals.answered)}%` : '';
  const learnedRecently = p.recentDays.filter(Boolean).length;

  // 消去完了の通知は常設の status に入れる。要素ごと挿入すると読み上げられないことがある。
  // 空のあいだも display:none にしない（非表示から可視に変わる live region は同じ問題に戻る）
  const clearedStatus = html`
    <p role="status" class="text-sm flex flex-wrap items-center gap-x-3 gap-y-1">
      ${p.clearedCount !== null
        ? html`<span
              >${p.clearedPersisted
                ? '学習の記録を消去しました'
                : 'この端末では保存していないので、今回の練習中の記録だけを消去しました'}</span
            >
            <button
              id="dq-clear-undo"
              type="button"
              @click=${p.onUndoClear}
              class="px-2 py-2 underline text-indigo-700 hover:text-indigo-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-700 rounded"
            >
              元に戻す
            </button>`
        : nothing}
    </p>
  `;

  const clearBlock = () => {
    if (p.clearedCount !== null) return nothing;
    if (p.confirmingClear) {
      return html`
        <div role="group" aria-label="学習の記録の消去の確認" class="text-sm space-y-2">
          <p>定着 ${p.totals.mastered} 字を含む ${recorded} 字ぶんの記録がすべて消えます</p>
          <div class="flex flex-wrap gap-3">
            <button
              id="dq-clear-cancel"
              type="button"
              @click=${p.onCancelClear}
              class="px-4 py-2 bg-white border border-gray-500 rounded-lg text-gray-800 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-700 focus-visible:ring-offset-2"
            >
              やめる
            </button>
            <button
              type="button"
              @click=${p.onConfirmClear}
              class="px-4 py-2 bg-gray-700 hover:bg-gray-800 text-white rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-700 focus-visible:ring-offset-2"
            >
              本当に消去する
            </button>
          </div>
        </div>
      `;
    }
    return html`
      <button
        type="button"
        ?disabled=${recorded === 0}
        @click=${p.onRequestClear}
        class="px-4 py-2 text-sm bg-white border border-gray-500 rounded-lg text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-700 focus-visible:ring-offset-2"
      >
        学習の記録を消去
      </button>
    `;
  };

  return html`
    <section
      id="dq-progress-panel"
      aria-labelledby=${HEADING_ID}
      class="mt-8 bg-indigo-50 rounded-xl p-5 text-gray-700 leading-relaxed space-y-5"
    >
      <h2
        id=${HEADING_ID}
        tabindex="-1"
        class="text-lg sm:text-xl font-semibold text-indigo-800 rounded scroll-mt-28 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-700 focus-visible:ring-offset-2"
      >
        学習の記録 (Progress)
      </h2>

      <p class="text-sm tabular-nums">
        累計 ${p.totals.answered} 問${accuracyText} ・ 学習した日 ${p.totals.days} 日 ・ 定着
        ${p.totals.mastered} 字
      </p>

      <div class="flex items-center gap-3 text-sm">
        <span aria-hidden="true">この 7 日</span>
        <span
          role="img"
          aria-label="この 7 日のうち学習した日は ${learnedRecently} 日"
          class="inline-flex items-center gap-1.5"
        >
          ${p.recentDays.map(
            (on) => html`<span
              class="dq-dot block w-3 h-3 rounded-full border ${on
                ? 'bg-teal-600 border-teal-600'
                : 'border-gray-500'}"
            ></span>`
          )}
        </span>
      </div>

      <div>
        <h3 class="text-sm font-medium text-indigo-800 mb-2">文字種ごとの内訳</h3>
        <div class="overflow-x-auto">
          <table class="w-full text-xs tabular-nums">
            <thead>
              <tr class="text-gray-600">
                <th scope="col" class="text-left font-normal pb-1">文字種</th>
                <th scope="col" class="text-right font-normal pb-1 pl-2">定着</th>
                <th scope="col" class="text-right font-normal pb-1 pl-2">学習中</th>
                <th scope="col" class="text-right font-normal pb-1 pl-2">曖昧</th>
                <th scope="col" class="text-right font-normal pb-1 pl-2">未学習</th>
              </tr>
            </thead>
            <tbody>
              ${p.breakdown.map(
                (r) => html`<tr class="border-t border-indigo-100">
                  <th scope="row" class="text-left font-normal py-1">${r.label}</th>
                  <td class="text-right py-1 pl-2">${r.mastered}</td>
                  <td class="text-right py-1 pl-2">${r.learning}</td>
                  <td class="text-right py-1 pl-2">${r.ambiguous}</td>
                  <td class="text-right py-1 pl-2">${r.unseen}</td>
                </tr>`
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h3 class="text-sm font-medium text-indigo-800 mb-2">まだ曖昧な字</h3>
        ${p.weak.length === 0
          ? html`<p class="text-sm">いまはありません</p>`
          : html`<ul class="flex flex-wrap gap-x-4 gap-y-2">
              ${p.weak.map(
                ({c, label}) => html`<li class="flex items-baseline gap-1.5">
                  <span lang="sa-Deva" class="font-devanagari text-2xl text-indigo-900">${c.char}</span>
                  <span class="text-indigo-700">=</span>
                  <span lang="sa-Latn" class="font-medium text-indigo-800">${c.romanized}</span>
                  <span class="text-xs text-gray-600">（${label}）</span>
                </li>`
              )}
            </ul>`}
      </div>

      <fieldset>
        <legend class="text-sm font-medium text-indigo-800 mb-2">今日の目標</legend>
        <div class="flex flex-wrap gap-4">
          ${GOALS.map(
            (g) => html`<label class="flex items-center gap-2 py-2 text-sm cursor-pointer">
              <input
                type="radio"
                name="dq-goal"
                value=${g}
                .checked=${live(p.goal === g)}
                @change=${() => p.onGoal(g)}
                class="accent-teal-600 w-4 h-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-700 focus-visible:ring-offset-2"
              />
              ${g} 問
            </label>`
          )}
        </div>
      </fieldset>

      <ul class="text-sm space-y-1 list-disc pl-5">
        <li>間違えた字や時間のかかった字を多めに出します。覚えた字も忘れないように時々出ます。</li>
        <li>「定着」は、別の日にもう一度正解した字です。同じ日に何度正解しても定着にはなりません。</li>
        <li>別の文字にする (New set) は出題する文字を選び直すだけで、学習の記録は消えません。</li>
      </ul>

      <!-- 反転（読み方から字を選ぶ向き）の切り替え。音声で使う人や片方向だけ練習したい人向け。
           パネルの中なので初期描画の高さには影響しない -->
      ${checkboxOption(
        'dq-reverse',
        '覚えた字は、読み方から字を選ぶ問題も出す',
        p.reverseEnabled,
        p.onReverse
      )}

      ${p.recordingAvailable
        ? checkboxOption(
            'dq-recording',
            'このブラウザに学習の記録を保存する',
            p.recordingEnabled,
            p.onRecording
          )
        : p.recordingReadOnly
          ? html`<p class="text-sm">
              新しい版で保存された記録があるため、このページでは記録を読み書きしません。今回の練習中だけ出題を調整します。
            </p>`
          : html`<p class="text-sm">
              この端末では学習の記録を保存できません（プライベートモードなど）。今回の練習中だけ出題を調整します。
            </p>`}

      <div class="space-y-2">${clearedStatus}${clearBlock()}</div>
    </section>
  `;
}
